import isArray from 'lodash/isArray';
import { useMutation, useQueryClient } from 'react-query';

import { UpdateItemCacheKeys, UpdateItemGqlQuery } from '../../../../../types';

import { updateEdItem } from '../../actions/updateEdItem';

import {
  ErrorType,
  parseRequestError
} from '../../../../../utils/parseRequestError';

interface UpdateQueryOptions<UpdateQueryInput, ItemType> {
  action: string;
  query: UpdateItemGqlQuery;
  cacheKeys?: UpdateItemCacheKeys;
  refetchTimeout?: number;
  onOptimisticUpdate?: (input: UpdateQueryInput) => null | (() => void);
  onSuccess?: (responseRecord: ItemType) => void;
  refetchInactive?: boolean;
}

interface UpdateQueryContext {
  rollback?: () => void;
}

function useEdUpdateQuery<
  UpdateQueryInput,
  UpdateQueryResponse,
  UpdateQueryError extends ErrorType,
  ItemType
>({
  action,
  query,
  cacheKeys,
  refetchTimeout,
  onOptimisticUpdate,
  onSuccess,
  refetchInactive = false
}: UpdateQueryOptions<UpdateQueryInput, ItemType>) {
  const queryClient = useQueryClient();

  const { data, error, isLoading, mutateAsync, reset } = useMutation<
    UpdateQueryResponse,
    UpdateQueryError,
    UpdateQueryInput,
    UpdateQueryContext
  >(
    (queryInput) =>
      updateEdItem<UpdateQueryInput, UpdateQueryResponse>({
        action,
        query,
        queryInput
      }),
    {
      onMutate: (input) => {
        const rollback = onOptimisticUpdate?.(input) || undefined;

        return { rollback };
      },
      onError: (error, variables, context) => {
        context?.rollback?.();
      },
      onSuccess: (response) => {
        onSuccess?.(response?.[action]?.record);
      },
      onSettled: async () => {
        if (isArray(cacheKeys)) {
          const refetchByCacheKeys = () =>
            cacheKeys.map((eachCacheKey) =>
              queryClient.invalidateQueries(eachCacheKey, {
                refetchInactive
              })
            );

          refetchTimeout
            ? window?.setTimeout(refetchByCacheKeys, refetchTimeout)
            : refetchByCacheKeys();
        }
      }
    }
  );

  return {
    updateQueryData: data,
    updateQueryError: error,
    updateQueryLoading: isLoading,
    updateQueryErrorMessage: parseRequestError(error),
    updateQuery: mutateAsync,
    updateQueryReset: reset
  };
}

export default useEdUpdateQuery;
